import { formatMoney } from 'utils'
import { type EmployersRenewalsRateSummary, type Money, type QuotingQuoteAllowance, type QuotingQuoteResult } from 'utils/web_api.schemas'

// This function recommends updated allowances based on insurance rate changes
// It uses a weighted average of the rate changes for the states that are not covered by state-specific allowances
// For ALE employers, it uses the highest min employer contribution for the state/default, if the rate-based amount is lower
export const recommendedFlatAllowances = (
  currentYearQuoteResults: QuotingQuoteResult[],
  currentYearAllowances: QuotingQuoteAllowance[],
  renewalYearQuoteResults: QuotingQuoteResult[],
  rateSummary: EmployersRenewalsRateSummary[],
  isEmployerALE?: boolean
) => {
  const allowanceStates = currentYearAllowances.map((allowance) => allowance.location_id).filter((state) => state !== null) as string[]

  const employeeCountByState = currentYearQuoteResults.reduce((acc, result) => {
    const state = result.rating_area_code.substring(0, 2)
    acc[state] = (acc[state] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const averageRateChangeByState = rateSummary.reduce((acc, summary) => {
    acc[summary.state_abbreviation] = summary.average_change
    return acc
  }, {} as Record<string, number>)

  const defaultAllowanceStates = Object.keys(employeeCountByState).filter(state => !allowanceStates.includes(state))
  const totalDefaultEmployees = defaultAllowanceStates.reduce((sum, state) => sum + employeeCountByState[state], 0)

  let minContributionByState: Record<string, number> = {}
  let minDefaultContribution: number = 0

  if (isEmployerALE) {
    const minContributions = minContributionsForQuoteResults(renewalYearQuoteResults, currentYearAllowances)
    minContributionByState = minContributions.minContributionByState
    minDefaultContribution = minContributions.minDefaultContribution
  }

  let weightedAverageForDefaultAllowance: number

  if (totalDefaultEmployees > 0) {
    // When there are default allowance states, use the weighted average of the rate changes for those states
    weightedAverageForDefaultAllowance = defaultAllowanceStates.reduce((weightedSum, state) => {
      const employeeCount = employeeCountByState[state]
      const rateChange = averageRateChangeByState[state] || 0
      return weightedSum + (rateChange * employeeCount)
    }, 0) / totalDefaultEmployees
  } else {
    // When all employees are covered by state-specific allowances, use the average rate change across all states
    const allStates = Object.keys(averageRateChangeByState)
    weightedAverageForDefaultAllowance = allStates.length > 0
      ? allStates.reduce((sum, state) => sum + averageRateChangeByState[state], 0) / allStates.length
      : 0
  }

  return currentYearAllowances.map((allowance) => {
    if (!allowance.employee_amount) {
      return allowance
    }

    let rateChange: number

    if (allowance.location_id) {
      rateChange = averageRateChangeByState[allowance.location_id] || 0
    } else {
      rateChange = weightedAverageForDefaultAllowance
    }

    const rateBased = {
      employee_amount: applyRateChange(allowance.employee_amount, rateChange),
      spouse_amount: applyRateChange(allowance.spouse_amount, rateChange),
      dependent_amount: applyRateChange(allowance.dependent_amount, rateChange)
    }

    if (isEmployerALE) {
      let minContribution: number

      if (allowance.location_id) {
        minContribution = minContributionByState[allowance.location_id] || 0
      } else {
        minContribution = minDefaultContribution
      }

      const minContributionSubunits = Math.ceil(minContribution) * 100

      const aleEmployeeAmount = Math.max(rateBased.employee_amount?.subunits || 0, minContributionSubunits)

      return {
        ...allowance,
        employee_amount: createMoney(aleEmployeeAmount),
        spouse_amount: rateBased.spouse_amount,
        dependent_amount: rateBased.dependent_amount
      }
    } else {
      return {
        ...allowance,
        ...rateBased
      }
    }
  })
}

export const minContributionsForQuoteResults = (quoteResults: QuotingQuoteResult[], allowances: QuotingQuoteAllowance[]) => {
  const allowanceStates = allowances.map((allowance) => allowance.location_id).filter((state) => state !== null) as string[]

  const minContributionByState = quoteResults.reduce((acc, result) => {
    const state = result.rating_area_code.substring(0, 2)
    const currentMax = acc[state] || 0
    acc[state] = Math.max(currentMax, result.min_employer_contribution)
    return acc
  }, {} as Record<string, number>)

  const minDefaultContribution = quoteResults.reduce((maxContribution, result) => {
    const state = result.rating_area_code.substring(0, 2)
    if (!allowanceStates.includes(state)) {
      return Math.max(maxContribution, result.min_employer_contribution)
    }
    return maxContribution
  }, 0)

  return {
    minContributionByState,
    minDefaultContribution,
  }
}

const createMoney = (subunits: number): Money => ({
  formatted_amount: formatMoney(subunits / 100),
  subunits,
  currency: 'USD',
})

const applyRateChange = (amount: Money | undefined, rateChange: number): Money | undefined => {
  if (!amount || !amount.subunits || rateChange <= 0) return amount

  const newAmount = amount.subunits * (1 + rateChange)
  const newSubunits = Math.ceil(newAmount / 100) * 100

  return createMoney(newSubunits)
}

export const allowancesAreEqual = (
  allowances1: QuotingQuoteAllowance[],
  allowances2: QuotingQuoteAllowance[]
): boolean => {
  if (allowances1.length !== allowances2.length) {
    return false
  }

  if (allowances1.length === 0) {
    return true
  }

  const map1 = createAllowanceMap(allowances1)
  const map2 = createAllowanceMap(allowances2)

  if (map1.size !== map2.size) {
    return false
  }

  for (const [locationId, allowance1] of map1) {
    const allowance2 = map2.get(locationId)

    if (!allowance2) {
      return false
    }

    const allowancesMatch = (
      allowance1.employee_amount?.subunits === allowance2.employee_amount?.subunits &&
      allowance1.spouse_amount?.subunits === allowance2.spouse_amount?.subunits &&
      allowance1.dependent_amount?.subunits === allowance2.dependent_amount?.subunits
    )

    if (!allowancesMatch) {
      return false
    }
  }

  return true
}

const createAllowanceMap = (allowances: QuotingQuoteAllowance[]) => {
  const map = new Map<string, QuotingQuoteAllowance>()
  allowances.forEach(allowance => {
    const key = allowance.location_id || 'default'
    map.set(key, allowance)
  })

  return map
}
